import { AlertTriangle } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { Field, FormError, SelectField } from '@/components/ui/field';
import { useSettings, useUpdateSetting } from '@/hooks/settings';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import {
  SETTING_GROUPS,
  SETTING_GROUP_HINTS,
  SETTING_GROUP_LABELS,
  type SettingView,
} from '@shared/schemas/setting';

/*
 * /admin/settings — business-rules.md §2. Owner decision, 2026-08-06.
 *
 * **A placeholder since phase 2, and no ROADMAP phase ever owned it.** Every
 * value in the `setting` table was edited with SQL until this screen; the table
 * has had an `updated_by_user_id` column since 3.1 with nothing to populate it.
 *
 * **One field saves at a time, deliberately.** A single Save for the whole page
 * would either send a dozen unchanged values on every click — writing an audit
 * row for each — or need change-tracking per field to avoid it. Twelve small
 * saves is the honest shape: each one is a rule changing, each gets its own
 * audit entry, and a mistyped tax rate cannot take the overtime rule with it.
 *
 * **The list comes from the server, not from here.** Which settings are
 * editable is `@shared/schemas/setting`'s registry, and it is shorter than the
 * table on purpose — six seeded keys are read by nothing and would be controls
 * that change nothing.
 */

export function SettingsPage() {
  const settings = useSettings();

  if (settings.isError) {
    return (
      <AdminPage title="Settings">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="Settings could not be loaded"
            onRetry={() => {
              void settings.refetch();
            }}
          />
        </div>
      </AdminPage>
    );
  }

  const rows = settings.data?.data ?? [];

  return (
    <AdminPage
      title="Settings"
      description="Business rules that change without a deploy. Only a super admin can edit these."
    >
      {settings.isPending ? (
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading the settings</span>
          <div aria-hidden="true" className="h-40 animate-pulse rounded-lg bg-white" />
          <div aria-hidden="true" className="h-40 animate-pulse rounded-lg bg-white" />
        </div>
      ) : (
        <div className="space-y-6">
          {SETTING_GROUPS.map((group) => {
            const inGroup = rows.filter((row) => row.group === group);
            if (inGroup.length === 0) return null;

            return (
              <section
                key={group}
                aria-labelledby={`settings-${group}`}
                className="rounded-lg border border-ink-300 bg-white p-5 shadow-card"
              >
                <h2 id={`settings-${group}`} className="text-h3 text-ink-900">
                  {SETTING_GROUP_LABELS[group]}
                </h2>
                <p className="mt-1 max-w-prose text-meta text-ink-500">
                  {SETTING_GROUP_HINTS[group]}
                </p>

                <div className="mt-5 space-y-6">
                  {inGroup.map((row) => (
                    /*
                      **Keyed on the stored value, so a save remounts the row.**
                      That is what re-initialises the field from what the server
                      actually kept — which can differ from what was typed, a
                      trimmed prefix being the obvious case. The alternative is
                      an effect that calls setState, which React now flags and
                      which is the same idea written less honestly.
                    */
                    <SettingRow key={`${row.key}:${toInputValue(row.value)}`} setting={row} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </AdminPage>
  );
}

/**
 * The stored value as something an input can hold.
 *
 * `value` is `unknown` because the column is `json`. Every editable key is a
 * string or a number by its registry schema, so anything else is a row somebody
 * edited by hand into a shape the screen cannot show — rendered as empty rather
 * than as `[object Object]`.
 */
function toInputValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function SettingRow({ setting }: { setting: SettingView }) {
  const toast = useToast();
  const update = useUpdateSetting();

  const [draft, setDraft] = useState(() => toInputValue(setting.value));

  const dirty = draft !== toInputValue(setting.value);
  const apiError = update.error instanceof ApiError ? update.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!dirty) return;

    // Numbers go over the wire as numbers. The registry says which keys are
    // numeric, and the server refuses a string where one belongs.
    const value: unknown =
      setting.kind === 'number' || setting.key === 'overtime_week_starts_on'
        ? Number(draft)
        : draft;

    update.mutate(
      { key: setting.key, value },
      { onSuccess: () => toast.success(`${setting.label} saved.`) },
    );
  }

  return (
    <form onSubmit={submit} className="border-t border-ink-300 pt-5 first:border-0 first:pt-0">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1">
          {setting.kind === 'select' ? (
            <SelectField
              label={setting.label}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              options={[...(setting.options ?? [])]}
              hint={setting.help}
            />
          ) : (
            <Field
              label={setting.label}
              name={setting.key}
              type={setting.kind === 'number' ? 'number' : 'text'}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              hint={setting.unit ? `${setting.help} (in ${setting.unit})` : setting.help}
            />
          )}
        </div>

        <Button type="submit" variant="outline" disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {setting.warning ? (
        <p className="mt-2 flex items-start gap-2 text-meta text-bark-900">
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {setting.warning}
        </p>
      ) : null}

      {apiError ? <FormError>{apiError.message}</FormError> : null}
    </form>
  );
}
